import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/ApiBase';
import TokenService from '../utils/tokenService';

const AuthContext = createContext(null);

// Cookie helper functions
const setCookie = (name, value, days) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${window.location.protocol === 'https:' ? ';Secure' : ''}`;
};

const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const removeCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);

    const checkAuth = useCallback(async () => {
        if (!initialized) {
            try {
                setLoading(true);
                setError(null);
                
                // Check token in localStorage for frontend state
                const token = TokenService.getToken();
                if (!token || !TokenService.isValidToken()) {
                    console.log('No valid token found in localStorage');
                    setUser(null);
                    setInitialized(true);
                    return;
                }

                // Get user info from backend
                const response = await axios.get(
                    `${API_BASE.API_BASE_URL}/users/me`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            ...TokenService.getAuthHeader()
                        },
                        withCredentials: true
                    }
                );

                if (response.data) {
                    console.log('User authenticated:', response.data);
                    setUser(response.data);
                } else {
                    console.log('No user data received');
                    setUser(null);
                    TokenService.removeToken();
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                setError(error.message);
                setUser(null);
                TokenService.removeToken();
            } finally {
                setLoading(false);
                setInitialized(true);
            }
        }
    }, [initialized]);

    // Check authentication status on mount
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const loginWithToken = useCallback(async (token, userData) => {
        try {
            setLoading(true);
            setError(null);

            if (!token) {
                throw new Error('Token is required');
            }

            if (!userData) {
                throw new Error('User data is required');
            }

            // Store token first
            if (!TokenService.setToken(token)) {
                throw new Error('Failed to store token');
            }

            // Then update user state
            setUser(userData);
            setInitialized(true);
            return { success: true };
        } catch (error) {
            console.error('Login with token error:', error);
            setError(error.message);
            setUser(null);
            TokenService.removeToken();
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            await axios.post(
                `${API_BASE.API_BASE_URL}/auth/logout`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...TokenService.getAuthHeader()
                    },
                    withCredentials: true
                }
            );

            setUser(null);
            TokenService.removeToken();
            setInitialized(true);
        } catch (error) {
            console.error('Logout error:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const login = useCallback(async (username, password) => {
        try {
            const response = await axios.post(
                `${API_BASE.API_BASE_URL}/auth/login`,
                { username, password },
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            if (response.data?.token) {
                if (!TokenService.setToken(response.data.token)) {
                    throw new Error('Failed to store token');
                }

                const userInfo = await axios.get(
                    `${API_BASE.API_BASE_URL}/users/me`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            ...TokenService.getAuthHeader()
                        },
                        withCredentials: true
                    }
                );

                if (userInfo.data) {
                    setUser(userInfo.data);
                    setInitialized(true);
                    return { success: true };
                }
            }
            return { success: false, error: 'Invalid response from server' };
        } catch (error) {
            TokenService.removeToken();
            return { 
                success: false, 
                error: error.response?.data?.message || 'Login failed. Please try again.' 
            };
        }
    }, []);

    const register = useCallback(async (userData) => {
        try {
            const response = await axios.post(
                `${API_BASE.API_BASE_URL}/auth/signup`,
                userData,
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            if (response.data) {
                return { success: true };
            }
            return { success: false, error: 'Registration failed' };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.message || 'Registration failed. Please try again.' 
            };
        }
    }, []);

    const value = React.useMemo(() => ({
        user,
        loading,
        error,
        initialized,
        isAuthenticated: !!user,
        login,
        logout,
        register,
        loginWithToken,
        checkAuth
    }), [user, loading, error, initialized, login, logout, register, loginWithToken, checkAuth]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 